import React, { useEffect, useState } from "react";
import { MUSCLE_GROUP_LABELS, ExerciseDetails } from "../../api/types.ts";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { RxQuestionMarkCircled } from "react-icons/rx";
import { Dropdown } from "../widgets/Dropdown.tsx";
import { DropdownItem } from "../widgets/DropdownItem.tsx";
import { WorkoutExerciseSets } from "./WorkoutExerciseSets.tsx";
import { MultiInlineEdit } from "../inputs/MultiInlineEdit.tsx";
import { Select } from "../inputs/Select.tsx";

import { IoIosArrowDown } from "react-icons/io";
import { Tooltip } from "../widgets/Tooltip.tsx";
import {
  ExerciseIcon,
  FetchedExercise,
  totalPowerPoints,
} from "../../util/exercise.tsx";
import { AddExerciseButton } from "../widgets/AddExerciseButton.tsx";
import { useDialog } from "../DialogManager.tsx";
import { isNotNullish } from "../../util/typeGuards.ts";

const WorkoutExerciseCard = ({
  exercise,
  index,
  focusedIndex,
  setFocusedIndex,
  editingExercise,
  setEditingExercise,
  onDeleteExerciseClick,
  onAddSet,
  onEditSet,
  availableExercises,
  availableExerciseSnapshots,
  onExerciseChange,
  totalExercises,
}: {
  exercise: ExerciseDetails;
  index: number;
  focusedIndex: number | null;
  setFocusedIndex: (index: number | null) => void;
  editingExercise: {
    index: number;
    exerciseId?: string;
    snapshotId?: string;
    resistanceUnit: "kg" | "lbs";
  } | null;
  setEditingExercise: (
    exercise: {
      index: number;
      exerciseId?: string;
      snapshotId?: string;
      resistanceUnit: "kg" | "lbs";
    } | null,
  ) => void;
  onDeleteExerciseClick: (exerciseIndex: number, exerciseName: string) => void;
  onAddSet: (
    workoutExerciseIndex: number,
    repetitions: number,
    resistance: number,
  ) => void;
  onEditSet: (
    workoutExerciseIndex: number,
    setIndex: number,
    repetitions: number,
    resistance: number,
  ) => void;
  availableExercises: Map<string, FetchedExercise>;
  availableExerciseSnapshots?: Map<string, ExerciseDetails>;
  onExerciseChange: (
    workoutExerciseIndex: number,
    newUnit: "kg" | "lbs",
    newExerciseId?: string,
    newSnapshotId?: string,
    newWorkoutExerciseIndex?: number,
  ) => void;
  totalExercises: number;
}) => {
  const [showExerciseDetails, setShowExerciseDetails] = useState(false);
  const [showToolTip, setShowToolTip] = useState(false);

  const handleSaveExerciseEdit = (index: number) => {
    if (!onExerciseChange || !setEditingExercise) {
      return;
    }

    if (editingExercise && editingExercise.index === index) {
      onExerciseChange(
        index,
        editingExercise.resistanceUnit,
        editingExercise.exerciseId,
        editingExercise.snapshotId,
      );
    }
    setEditingExercise(null);
  };

  const handleCancelExerciseEdit = (index: number) => {
    if (!setEditingExercise) {
      return;
    }

    setEditingExercise(null);
  };

  return (
    <div className="bg-gray-50 rounded-md p-3">
      <div className="flex justify-between items-start">
        <div className="w-full">
          <div
            className="flex flex-wrap items-center gap-2 w-full justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col">
              <div className="flex flex-row items-center gap-2 flex-1">
                {focusedIndex !== index && <ExerciseIcon exercise={exercise} />}
                <MultiInlineEdit
                  displayValue={exercise.name}
                  className="flex flex-wrap gap-2"
                  displayClassName="text-gray-800 font-semibold"
                  onSave={() => handleSaveExerciseEdit(index)}
                  onCancel={() => handleCancelExerciseEdit(index)}
                  displayAs="h5"
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={() => setFocusedIndex(null)}
                >
                  <Select
                    className="max-w-36 sm:max-w-64"
                    value={
                      editingExercise?.index === index
                        ? (editingExercise.exerciseId ??
                          (editingExercise.snapshotId
                            ? `snapshot-${editingExercise.snapshotId}`
                            : ""))
                        : ""
                    }
                    options={Array.from(availableExercises.entries())
                      .map((entry) => ({
                        id: entry[0],
                        label: entry[1]?.exercise?.name ?? "Unknown Exercise",
                      }))
                      .concat(
                        availableExerciseSnapshots
                          ? Array.from(
                              availableExerciseSnapshots.entries(),
                            ).map((entry) => ({
                              id: `snapshot-${entry[0]}`,
                              label: `${entry[1].name} (snapshot)`,
                              style: { fontStyle: "italic" },
                            }))
                          : [],
                      )}
                    onChange={(newId) => {
                      setEditingExercise({
                        index,
                        exerciseId: newId.startsWith("snapshot")
                          ? undefined
                          : newId,
                        snapshotId: newId.startsWith("snapshot")
                          ? newId.slice(9)
                          : undefined,
                        resistanceUnit:
                          editingExercise?.resistanceUnit ||
                          exercise.resistance_unit,
                      });
                    }}
                  />
                  <Select
                    className="max-w-18"
                    value={
                      editingExercise?.index === index
                        ? editingExercise.resistanceUnit
                        : exercise.resistance_unit
                    }
                    options={["kg", "lbs"]}
                    onChange={(value) => {
                      setEditingExercise({
                        index,
                        exerciseId: editingExercise?.exerciseId || "",
                        resistanceUnit: value as "kg" | "lbs",
                      });
                    }}
                  />
                </MultiInlineEdit>
              </div>
            </div>
          </div>
          <WorkoutExerciseSets
            exercise={exercise}
            workoutExerciseIndex={index}
            onAddSet={onAddSet}
            onEditSet={onEditSet}
          />
        </div>

        <div
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Dropdown
            trigger={
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Exercise options"
              >
                <HiOutlineDotsVertical className="text-gray-600" size={20} />
              </button>
            }
            alignment="left"
          >
            {index > 0 && (
              <DropdownItem
                onClick={() => {
                  onExerciseChange(
                    index,
                    exercise.resistance_unit,
                    undefined,
                    undefined,
                    index - 1,
                  );
                }}
                className="hover:bg-grey-50"
              >
                Move Up
              </DropdownItem>
            )}
            {index < totalExercises - 1 && (
              <DropdownItem
                onClick={() => {
                  onExerciseChange(
                    index,
                    exercise.resistance_unit,
                    undefined,
                    undefined,
                    index + 1,
                  );
                }}
                className="hover:bg-grey-50"
              >
                Move Down
              </DropdownItem>
            )}
            <DropdownItem
              onClick={() => onDeleteExerciseClick(index, exercise.name)}
              className="text-red-600 hover:bg-red-50"
            >
              Delete
            </DropdownItem>
          </Dropdown>
        </div>
      </div>
      <div
        className="w-full flex flex-row mt-2 items-center"
        aria-label="Show performance details"
      >
        <IoIosArrowDown
          className="inline-block transition-transform duration-300 text-gray-400"
          style={{
            transform: showExerciseDetails ? "rotate(180deg)" : "rotate(0deg)",
          }}
          onClick={() => setShowExerciseDetails((prev) => !prev)}
        />
        <div
          className={`${showExerciseDetails ? "border-b" : ""} w-full mx-2 border-gray-300`}
        />
      </div>
      {showExerciseDetails && (
        <div className="mt-2 flex flex-col text-gray-600">
          <div
            className="flex w-min"
            onMouseEnter={() => setShowToolTip(true)}
            onMouseLeave={() => setShowToolTip(false)}
          >
            <Tooltip
              show={showToolTip}
              label="The Performance Points are an accumulation of the total sets and the muscle group weights defined in the exercise."
              alignRight
            >
              <div className="flex flex-row gap-1">
                <span className="text-xs mb-1">Performance</span>
                <RxQuestionMarkCircled className="p-0.5 " />
              </div>
            </Tooltip>
          </div>
          {exercise.primary_muscle_groups.map((muscle) => (
            <span className="text-xs">
              <span className="font-bold">
                {MUSCLE_GROUP_LABELS[muscle.muscle_group]}
              </span>
              : {Math.round(totalPowerPoints(exercise) * muscle.weight)} points
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

interface WorkoutExerciseListProps {
  resourceId: string;
  workoutExercises: ExerciseDetails[];
  availableExercises: Map<string, FetchedExercise>;
  availableExerciseSnapshots?: Map<string, ExerciseDetails>;
  onExerciseChange: (
    workoutExerciseIndex: number,
    newUnit: "kg" | "lbs",
    newExerciseId?: string,
    newSnapshotId?: string,
    newWorkoutExerciseIndex?: number,
  ) => void;
  onDeleteExercise: (workoutExerciseIndex: number) => void;
  onAddExercise: (exerciseId: string) => void;
  onAddSet: (
    workoutExerciseIndex: number,
    repetitions: number,
    resistance: number,
  ) => void;
  onEditSet: (
    workoutExerciseIndex: number,
    setIndex: number,
    repetitions: number,
    resistance: number,
  ) => void;
}

const WorkoutExerciseList: React.FC<WorkoutExerciseListProps> = ({
  resourceId,
  workoutExercises,
  availableExercises,
  availableExerciseSnapshots,
  onExerciseChange,
  onDeleteExercise,
  onAddExercise,
  onAddSet,
  onEditSet,
}) => {
  const { openDialog } = useDialog();

  const [editingExercise, setEditingExercise] = useState<{
    index: number;
    exerciseId?: string;
    snapshotId?: string;
    resistanceUnit: "kg" | "lbs";
  } | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isNotNullish(focusedIndex)) {
      return;
    }

    const exercise = workoutExercises[focusedIndex];

    setEditingExercise({
      index: focusedIndex,
      snapshotId: exercise.snapshot_id,
      resistanceUnit: exercise.resistance_unit,
    });
  }, [focusedIndex, workoutExercises]);

  const handleDeleteExerciseClick = (
    exerciseIndex: number,
    exerciseName: string,
  ) => {
    openDialog("confirm", {
      onConfirm: () => onDeleteExercise(exerciseIndex),
      title: "Remove Exercise from Workout",
      message: `Are you sure you want to remove "${exerciseName}" from this workout?`,
      confirmText: "Remove",
      variant: "danger",
    });
  };

  return (
    <>
      <div key={resourceId} className="space-y-3">
        {workoutExercises.map((exerciseDetails, index) => (
          <div key={`${resourceId}-${index}`}>
            <WorkoutExerciseCard
              exercise={exerciseDetails}
              index={index}
              focusedIndex={focusedIndex}
              setFocusedIndex={setFocusedIndex}
              editingExercise={editingExercise}
              setEditingExercise={setEditingExercise}
              onDeleteExerciseClick={handleDeleteExerciseClick}
              onAddSet={onAddSet}
              onEditSet={onEditSet}
              availableExercises={availableExercises}
              availableExerciseSnapshots={availableExerciseSnapshots}
              onExerciseChange={onExerciseChange}
              totalExercises={workoutExercises.length}
            />
          </div>
        ))}
      </div>

      <AddExerciseButton
        availableExercises={availableExercises}
        isEditable={true}
        onAddExercise={onAddExercise}
      />
    </>
  );
};

export { WorkoutExerciseList };
